import { useState } from 'react'
import { ArrowRight, ScanLine } from 'lucide-react'
import { cn } from '@/lib/utils'
import { hhmm, ivPct, money } from '../utils/format'
import { snapshotComparison } from '../utils/snapshotComparison'
import type { JournalTrade } from '../types'

const number = (value: number | null, digits: number) => value === null
  ? '—'
  : value.toLocaleString('en-IN', { minimumFractionDigits: digits, maximumFractionDigits: digits })

export function MarketSnapshots({ trade }: { trade: JournalTrade }) {
  const [legId, setLegId] = useState<string | null>(null)
  const selected = trade.legs.find((leg) => leg.tradeLegId === legId)
  const legs = selected ? [selected] : trade.legs
  const metrics = snapshotComparison(legs, selected ? 'LEG' : 'TRADE')
  const noIv = trade.ivAtEntry === null && trade.ivAtExit === null

  return (
    <section className="jn-panel" aria-label="Entry and exit market snapshots">
      <header className="jn-panel-heading">
        <div className="flex items-center gap-2">
          <ScanLine size={14} className="text-primary" />
          <h3>Market snapshots</h3>
        </div>
        <span className="jn-caption" title={selected
          ? `Per option unit. Recorded ${hhmm(selected.entrySnapAt ?? null)} → ${hhmm(selected.exitSnapAt ?? null)} IST. OI ${number(selected.entryOi, 0)} → ${number(selected.exitOi, 0)}.`
          : 'Reference exposure: opening side × recorded quantity. IV is the arithmetic mean; every leg is required.'}>
          Entry <ArrowRight size={11} /> Exit
        </span>
        {noIv && <span className="jn-badge" data-testid="journal-no-iv">Not recorded</span>}
      </header>
      <div className="jn-snapshot-scopes" role="group" aria-label="Snapshot scope">
        <button
          type="button"
          aria-pressed={!selected}
          onClick={() => setLegId(null)}
          className={cn('jn-scope', !selected && 'is-active')}
        >
          Trade total
        </button>
        {trade.legs.map((leg, index) => (
          <button
            key={leg.tradeLegId}
            type="button"
            aria-pressed={selected?.tradeLegId === leg.tradeLegId}
            onClick={() => setLegId(leg.tradeLegId)}
            title={leg.symbol}
            aria-label={`Snapshots for leg ${index + 1}: ${leg.symbol}`}
            className={cn('jn-scope', selected?.tradeLegId === leg.tradeLegId && 'is-active')}
          >
            <span className="text-[9px] text-muted-foreground">{index + 1}</span>
            <span className={cn('jn-side', leg.side === 'BUY' ? 'is-buy' : 'is-sell')}>
              {leg.side === 'BUY' ? 'B' : 'S'}
            </span>
            <span className="num">{leg.strike.toLocaleString('en-IN')} {leg.optionType}</span>
          </button>
        ))}
      </div>
      <div className="jn-snapshot-grid">
        {metrics.map((metric) => (
          <div key={metric.key} className="jn-snapshot" data-testid={`journal-snapshot-${metric.key}`}>
            {/*
              The unit rides with the name — 9.54. These same five boxes
              show PER-UNIT greeks on a leg tab and POSITION greeks on the
              trade tab, figures that differ by the size of the basket, and
              under one label a click looked like a bug.
            */}
            <div className="jn-snapshot-name">
              <span>{metric.label}</span>
              <span>
                {metric.name.split(' · ')[0]}
                {metric.name.includes(' · ') && (
                  <span className="jn-snapshot-unit"> · {metric.name.split(' · ').slice(1).join(' · ')}</span>
                )}
              </span>
            </div>
            <dl>
              {(['entry', 'exit'] as const).map((phase) => (
                <div key={phase}>
                  <dt>{phase === 'entry' ? 'Entry' : 'Exit'}</dt>
                  <dd className="num" title={`${metric[phase].recorded}/${legs.length} legs recorded`}>
                    {/*
                      MONEY IS DRAWN AS MONEY — 9.56. Position theta and
                      vega are rupees, and `money` is what every other
                      rupee figure on this page already uses, so ₹11.7 L
                      reads at a glance where 11,73,140 had to be counted.
                    */}
                    {metric.currency && metric[phase].value !== null
                      ? money(metric[phase].value)
                      : number(metric[phase].value, metric.digits)}
                    {metric.key === 'iv' && metric[phase].value !== null ? '%' : ''}
                  </dd>
                </div>
              ))}
            </dl>
            <div className="jn-snapshot-change num">
              {metric.entry.value === null || metric.exit.value === null
                ? 'Not recorded'
                : metric.currency
                  ? `${money(metric.exit.value - metric.entry.value)} change`
                  : `${metric.exit.value - metric.entry.value >= 0 ? '+' : ''}${number(metric.exit.value - metric.entry.value, metric.digits)}${metric.key === 'iv' ? ' pp' : ''} change`}
            </div>
          </div>
        ))}
      </div>
      {!noIv && <span className="sr-only" data-testid="journal-iv">IV {ivPct(trade.ivAtEntry)} → {ivPct(trade.ivAtExit)}</span>}
    </section>
  )
}
